import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown, faChevronUp } from '@fortawesome/free-solid-svg-icons';
import { getCandidatesForInverterModel, getCandidatesForMagazineModel } from '../../api/compatibility';
import { formatKwota } from '../../utils';
import { ProductImage } from '../ProductImage';
import AvailabilityBadge from '../AvailabilityBadge';

const PREVIEW_COUNT = 5;

// Odczyt defensywny — backend listy modelowej może zwracać pojemność/moc pod
// różnymi nazwami (albo jeszcze wcale). Brak wartości => pole pomijane, filtr obojętny.
const rowCapacity = (r) => r.capacity_kwh ?? r.capacity ?? null;
const rowPower = (r) => r.power_kw ?? r.power ?? null;
const rowPhase = (r) => r.phase ?? null;

// Wiersz kandydata na poziomie MODELU katalogowego — miniatura + nazwa + producent
// (+ pojemność / moc, gdy backend je zwraca) + cena/dostępność. Dwa tryby:
//  - `onSelectModel` (strona /kompatybilnosc): klik w wiersz WYBIERA ten model jako
//    drugie urządzenie (od razu wynik pary), a niebieski „Sprawdź dostępność" -> model.
//  - bez `onSelectModel` (strona modelu): cały wiersz jest linkiem do modelu.
const RowBody = ({ item, imgType, specText }) => {
  const { t } = useTranslation('model');
  return (
  <>
    <div className="w-11 h-11 shrink-0 flex items-center justify-center rounded-lg border border-gray-100 bg-white p-1">
      <ProductImage src={item.img_url} productType={imgType} alt={item.product_name} className="max-w-full max-h-full object-contain" />
    </div>
    <div className="min-w-0 flex-1">
      <p className="text-sm font-medium text-gray-800 truncate">{item.product_name}</p>
      <p className="text-xs text-gray-400 truncate">
        {[item.manufacturer, specText, item.short_condition_pl].filter(Boolean).join(' · ')}
      </p>
    </div>
    <div className="hidden sm:flex flex-col items-end shrink-0 gap-1">
      {item.price_min != null && <span className="text-sm font-semibold text-blue-600">{t('kompat.od_kwoty', { kwota: formatKwota(item.price_min) })}</span>}
      {item.availability_any ? <AvailabilityBadge value="Dostępny" /> : <span className="text-xs text-gray-400">{t('kompat.sprawdz_oferty')}</span>}
    </div>
  </>
  );
};

const ModelRow = ({ item, onSelectModel, imgType }) => {
  const { t } = useTranslation('model');
  const cap = rowCapacity(item);
  const pw = rowPower(item);
  const ph = rowPhase(item);
  const specText = imgType === 'magazyn'
    ? (cap != null ? `${cap} kWh` : null)
    // Faza przychodzi jako wartość katalogu („3 Fazowy”); tłumaczymy tylko napis.
    : [pw != null ? `${pw} kW` : null, ph ? t(`wartosc.faza.${ph}`, { defaultValue: ph }) : null].filter(Boolean).join(' · ') || null;

  if (onSelectModel) {
    return (
      <li className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => onSelectModel(item)}
          className="flex items-center gap-3 flex-1 min-w-0 py-2.5 px-2 -mx-1 rounded-lg hover:bg-gray-50 text-left transition-colors"
        >
          <RowBody item={item} imgType={imgType} specText={specText} />
        </button>
        <Link
          to={item.model_url}
          onClick={(e) => e.stopPropagation()}
          className="shrink-0 inline-flex items-center px-3 py-1.5 rounded-lg bg-blue-600 text-white text-xs font-semibold hover:bg-blue-500 transition-colors"
        >
          {t('kompat.sprawdz_dostepnosc')}
        </Link>
      </li>
    );
  }
  return (
    <li>
      <Link to={item.model_url} className="flex items-center gap-3 py-2.5 px-3 -mx-3 rounded-lg hover:bg-gray-50 transition-colors">
        <RowBody item={item} imgType={imgType} specText={specText} />
      </Link>
    </li>
  );
};

const StatusGroup = ({ title, rows, onSelectModel, imgType }) => {
  const { t } = useTranslation('model');
  const [expanded, setExpanded] = useState(false);
  if (!rows.length) return null;
  const visible = expanded ? rows : rows.slice(0, PREVIEW_COUNT);
  return (
    <div className="mb-5 last:mb-0">
      <h3 className="text-xs font-bold uppercase tracking-wide text-gray-400 mb-1.5">{title} ({rows.length})</h3>
      <ul className="divide-y divide-gray-100">
        {visible.map((item) => <ModelRow key={item.id} item={item} onSelectModel={onSelectModel} imgType={imgType} />)}
      </ul>
      {rows.length > PREVIEW_COUNT && !expanded && (
        <button type="button" onClick={() => setExpanded(true)} className="mt-1.5 text-xs font-semibold text-blue-600 hover:underline">
          {t('kompat.pokaz_wszystkie', { liczba: rows.length })}
        </button>
      )}
    </div>
  );
};

// Panel kompatybilności — zasilany endpointami modelowymi (Kontekst 2). Domyślnie
// sortuje po najniższej cenie (pozycje bez ceny lądują na końcu — patrz byPriceAsc).
// Filtry (klient, na już pobranych danych):
//  - magazyny (direction 'inverter'): zakres pojemności od/do,
//  - falowniki (direction 'battery'): producent + dokładna moc + liczba faz.
// Filtry po polach, których backend jeszcze nie zwraca (pojemność/moc/faza), są
// obojętne dopóki tych pól nie ma (brak wartości nie wyklucza wiersza).
// `collapsedByDefault` — na stronie modelu panel startuje zwinięty do niskiego
// paska (tytuł + liczby), żeby nie zajmować ekranu zanim user faktycznie chce
// zobaczyć listę; na /kompatybilnosc (bare) zostaje od razu rozwinięty.
// `headless` — oddaj SAMĄ TREŚĆ, bez karty i bez własnego nagłówka. Używa tego
// ProductPanels, który trzyma wspólny pasek zakładek na stronie modelu; bez tego
// mielibyśmy nagłówek w nagłówku i dwa niezależne stany zwinięcia.
// `onSummary` — komponent sam liczy pozycje, więc musi oddać tę liczbę w górę,
// żeby wołający mógł ją pokazać przy etykiecie zakładki.
const ModelCompatibilityList = ({ direction, modelId, bare = false, onSelectModel, collapsedByDefault = false, headless = false, onSummary }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [capMin, setCapMin] = useState('');
  const [capMax, setCapMax] = useState('');
  const [mfr, setMfr] = useState('');
  const [power, setPower] = useState('');
  const [phase, setPhase] = useState('');
  const [expanded, setExpanded] = useState(!collapsedByDefault);
  const { t } = useTranslation('model');

  const fetchFn = direction === 'inverter' ? getCandidatesForInverterModel : getCandidatesForMagazineModel;
  // direction 'inverter' -> lista magazynów, 'battery' -> lista falowników.
  const rowImgType = direction === 'inverter' ? 'magazyn' : 'inwerter';

  useEffect(() => {
    if (modelId == null) return undefined;
    let cancelled = false;
    setLoading(true);
    setError(false);
    // Świeże filtry przy zmianie modelu/kierunku.
    setCapMin(''); setCapMax(''); setMfr(''); setPower(''); setPhase('');
    fetchFn(modelId)
      .then((res) => { if (!cancelled) { setData(res); setLoading(false); } })
      .catch(() => { if (!cancelled) { setError(true); setLoading(false); } });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [direction, modelId]);

  // Podsumowanie dla wołającego rysującego własny nagłówek. MUSI iść przez
  // useEffect, a nie wywołanie w renderze: to zmiana stanu rodzica, więc w trakcie
  // renderu Reakt zgłosiłby ostrzeżenie i groziłaby pętla.
  useEffect(() => {
    if (!onSummary) return;
    const g = data?.groups || {};
    const n = (g.compatible?.length || 0) + (g.conditional?.length || 0) + (g.conflicting?.length || 0);
    onSummary(loading && !data
      ? t('kompat.wczytywanie')
      : error ? null : n === 0 ? t('kompat.brak_danych') : t('kompat.pozycje', { count: n }));
  }, [data, loading, error, onSummary, t]);

  if (modelId == null) return null;

  const copy = direction === 'inverter'
    ? { title: t('panele.kompat_magazyny'), empty: t('kompat.pusto_magazyny') }
    : { title: t('kompat.tytul_falowniki'), empty: t('kompat.pusto_falowniki') };

  const groups = data?.groups || {};
  const rawGroups = [groups.compatible, groups.conditional, groups.conflicting];
  const rawCount = rawGroups.reduce((n, g) => n + (g?.length || 0), 0);

  // Producenci do filtra falowników — z faktycznie zwróconych wierszy (bez extra fetch).
  const manufacturerOptions = direction === 'battery'
    ? [...new Set(rawGroups.flatMap((g) => g || []).map((r) => r.manufacturer).filter(Boolean))].sort()
    : [];

  const passesFilter = (r) => {
    if (direction === 'inverter') {
      const cap = rowCapacity(r);
      const min = capMin ? parseFloat(capMin) : null;
      const max = capMax ? parseFloat(capMax) : null;
      if (cap == null) return true; // pojemność nieznana -> nie wykluczamy
      if (min != null && cap < min) return false;
      if (max != null && cap > max) return false;
      return true;
    }
    if (mfr && r.manufacturer !== mfr) return false;
    if (power) {
      const pw = rowPower(r);
      if (pw != null && String(pw) !== String(power)) return false; // moc nieznana -> nie wykluczamy
    }
    if (phase) {
      const ph = rowPhase(r);
      if (ph != null && ph !== phase) return false; // faza nieznana -> nie wykluczamy
    }
    return true;
  };

  const byPriceAsc = (a, b) => {
    const pa = a.price_min == null ? Infinity : a.price_min;
    const pb = b.price_min == null ? Infinity : b.price_min;
    return pa - pb;
  };
  const prep = (rows) => (rows || []).filter(passesFilter).sort(byPriceAsc);

  const gCompatible = prep(groups.compatible);
  const gConditional = prep(groups.conditional);
  const gConflicting = prep(groups.conflicting);
  const shownCount = gCompatible.length + gConditional.length + gConflicting.length;

  const inputCls = 'px-3 py-1.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500';

  const summary = loading && !data
    ? t('kompat.wczytywanie')
    : error
      ? null
      : rawCount === 0
        ? t('kompat.brak_danych')
        : t('kompat.pozycje', { count: rawCount });

  const tresc = (
    <>
            {rawCount > 0 && (
              <div className="flex flex-wrap items-center justify-end gap-2 mb-4">
                {direction === 'inverter' ? (
                  <>
                    <input type="number" inputMode="decimal" placeholder={t('kompat.pojemnosc_od')} value={capMin} onChange={(e) => setCapMin(e.target.value)} aria-label={t('kompat.pojemnosc_od')} className={`w-36 ${inputCls}`} />
                    <input type="number" inputMode="decimal" placeholder={t('kompat.pojemnosc_do')} value={capMax} onChange={(e) => setCapMax(e.target.value)} aria-label={t('kompat.pojemnosc_do')} className={`w-36 ${inputCls}`} />
                  </>
                ) : (
                  <>
                    <div className="relative">
                      <select value={mfr} onChange={(e) => setMfr(e.target.value)} aria-label={t('pole.producent')} className={`appearance-none pl-3 pr-8 ${inputCls}`}>
                        <option value="">{t('kompat.wszyscy_producenci')}</option>
                        {manufacturerOptions.map((m) => <option key={m} value={m}>{m}</option>)}
                      </select>
                      <FontAwesomeIcon icon={faChevronDown} className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 text-xs" />
                    </div>
                    <input type="number" inputMode="decimal" placeholder={t('kompat.moc_kw')} value={power} onChange={(e) => setPower(e.target.value)} aria-label={t('kompat.moc_kw')} className={`w-28 ${inputCls}`} />
                    <div className="relative">
                      <select value={phase} onChange={(e) => setPhase(e.target.value)} aria-label={t('el.liczba_faz')} className={`appearance-none pl-3 pr-8 ${inputCls}`}>
                        <option value="">{t('kompat.dowolna_liczba_faz')}</option>
                        <option value="1 Fazowy">{t('wartosc.faza.1 Fazowy')}</option>
                        <option value="3 Fazowy">{t('wartosc.faza.3 Fazowy')}</option>
                      </select>
                      <FontAwesomeIcon icon={faChevronDown} className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 text-xs" />
                    </div>
                  </>
                )}
              </div>
            )}

            {loading && !data ? (
              <div className="space-y-3 animate-pulse" aria-busy="true" aria-label={t('kompat.wczytywanie_listy')}>
                {[0, 1, 2].map((i) => <div key={i} className="h-10 bg-gray-100 rounded-lg" />)}
              </div>
            ) : error ? (
              <p className="text-sm text-rose-500">{t('kompat.blad')}</p>
            ) : rawCount === 0 ? (
              <p className="text-sm text-gray-400">{copy.empty}</p>
            ) : shownCount === 0 ? (
              <p className="text-sm text-gray-400">{t('kompat.brak_dla_filtrow')}</p>
            ) : (
              <div>
                <StatusGroup title={t('kompat.status.kompatybilne')} rows={gCompatible} onSelectModel={onSelectModel} imgType={rowImgType} />
                <StatusGroup title={t('kompat.status.warunkowo')} rows={gConditional} onSelectModel={onSelectModel} imgType={rowImgType} />
                <StatusGroup title={t('kompat.status.sprzeczne')} rows={gConflicting} onSelectModel={onSelectModel} imgType={rowImgType} />
              </div>
            )}
    </>
  );

  // Sama treść — kartę, nagłówek i zwijanie zapewnia wołający (ProductPanels).
  if (headless) return tresc;

  return (
    <div className={bare ? '' : 'max-w-7xl mx-auto px-4 md:px-8 mt-6'}>
      <div className="bg-white rounded-2xl border border-gray-200 shadow-sm">
        {/* Nagłówek — zawsze widoczny, klikalny pasek zwijający/rozwijający panel. */}
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          aria-expanded={expanded}
          className="w-full flex items-center justify-between gap-3 px-5 md:px-7 py-4 text-left rounded-2xl hover:bg-gray-50/60 transition-colors"
        >
          <span className="flex items-center gap-2 min-w-0">
            <h2 className="text-sm font-bold text-gray-900 truncate">{copy.title}</h2>
            {summary && <span className="text-xs text-gray-400 shrink-0">{summary}</span>}
          </span>
          <FontAwesomeIcon icon={expanded ? faChevronUp : faChevronDown} className="text-gray-400 text-xs shrink-0" />
        </button>

        {expanded && <div className="px-5 md:px-7 pb-5 md:pb-7">{tresc}</div>}
      </div>
    </div>
  );
};

export default ModelCompatibilityList;
